import { useEffect, useRef } from "react";

type Args = {
  isLoading: boolean;
  onTick: (nextText: string) => void;
};

const NETWORK_BUSY_TEXT = "ネットワークが混雑しています";
const SLOW_NOTICE_TEXT =
  "通常より時間がかかっています。しばらくお待ちください";

const NETWORK_BUSY_AFTER_MS = 30_000;
const SLOW_NOTICE_AFTER_MS = 40_000;

const baseSteps = [
  "ユーザーからの質問の背景や意図を読み取っています",
  "広告配信状況・計測データの解析準備を進めています",
  "各媒体の最新データを横断的に再取得しています",
  "分析結果を統合し、最適な回答を生成しています",
  "過去の類似ケースと照合し、改善余地を洗い出しています",
  "指標ごとの影響度を評価し、優先順位を整理しています",
  "実行可能性とリスクを考慮しながら提案内容を調整しています",
  "最終的な回答文面を整えています",
  "配信条件やターゲット設定の妥当性を再確認しています",
  "直近のパフォーマンス変動要因を時系列で追っています",
  "改善施策の効果見込みをシミュレーションしています",
];

type Phase = "normal" | "networkBusy" | "slowNotice";

export function useLoadingSteps({ isLoading, onTick }: Args) {
  const timerRef = useRef<number | null>(null);
  const stepIndexRef = useRef(0);
  const stepsRef = useRef<string[]>([]);
  const loadingStartRef = useRef<number | null>(null);
  const phaseRef = useRef<Phase>("normal");

  const getRandomDelay = () => {
    const min = 2500;
    const max = 4000;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const clear = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const shuffle = (arr: string[]) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const loop = () => {
    timerRef.current = window.setTimeout(() => {
      if (!loadingStartRef.current) return;

      const elapsed = Date.now() - loadingStartRef.current;

      // 30秒超え：最終状態（固定）
      if (elapsed >= SLOW_NOTICE_AFTER_MS) {
        if (phaseRef.current !== "slowNotice") {
          phaseRef.current = "slowNotice";
          onTick(SLOW_NOTICE_TEXT);
        }
        clear(); // ← ここだけで止める
        return;
      }

      // 20〜30秒：ネットワーク混雑（固定だがループ継続）
      if (elapsed >= NETWORK_BUSY_AFTER_MS) {
        if (phaseRef.current !== "networkBusy") {
          phaseRef.current = "networkBusy";
          onTick(NETWORK_BUSY_TEXT);
        }
        loop(); // ← 次のtickを予約（30秒判定のため）
        return;
      }

      // 0〜20秒：通常ステップ
      phaseRef.current = "normal";
      stepIndexRef.current =
        (stepIndexRef.current + 1) % stepsRef.current.length;

      onTick(stepsRef.current[stepIndexRef.current]);
      loop();
    }, getRandomDelay());
  };

  useEffect(() => {
    clear();

    if (isLoading) {
      loadingStartRef.current = Date.now();
      phaseRef.current = "normal";
      stepIndexRef.current = 0;

      const [first, ...rest] = baseSteps;
      stepsRef.current = [first, ...shuffle(rest)];

      onTick(stepsRef.current[0]);
      loop();
    } else {
      loadingStartRef.current = null;
      phaseRef.current = "normal";
    }

    return clear;
  }, [isLoading]);

  return { initialLoadingText: baseSteps[0] };
}
